// ============================================
// App.jsx - GhostInk AI Main Application
// ============================================
// Three-column layout chat application
// Connected to Django backend with auth & fingerprinting

import React, { useState, useCallback, useEffect } from 'react';
import {
  ThreeColumnLayout,
  SessionSidebar,
  RightSidebarPanel,
  ChatInterface,
  SettingsModal,
} from './components';
import { useAuth, useChat } from './hooks';
import AuthModal from './components/AuthModal';

// ============================================
// MAIN APP COMPONENT
// ============================================
export default function App() {
  // ============================================
  // AUTH STATE (from hook)
  // ============================================
  const {
    user,
    usage,
    loading: authLoading,
    isLoggedIn,
    login,
    register,
    logout,
    refreshUsage,
  } = useAuth();

  // ============================================
  // CHAT STATE (from hook)
  // ============================================
  const {
    sessions,
    activeSession,
    messages,
    isLoading,
    isSending,
    error: chatError,
    initialLoading,
    refreshSessions,
    selectSession,
    newSession,
    deleteSession,
    pinSession,
    archiveSession,
    renameSession,
    sendMessage,
    clearError,
  } = useChat(refreshUsage);

  // ============================================
  // LAYOUT STATE
  // ============================================
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [rightSidebarExpanded, setRightSidebarExpanded] = useState(false);
  const [leftWidth, setLeftWidth] = useState(280);
  const [rightWidth, setRightWidth] = useState(360);

  // ============================================
  // AUTH MODAL STATE
  // ============================================
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin'); // 'signin' or 'signup'

  // ============================================
  // SETTINGS MODAL STATE
  // ============================================
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // ============================================
  // LOCAL UI STATE
  // ============================================
  const [input, setInput] = useState('');
  const [documents, setDocuments] = useState([]);

  // ============================================
  // AUTH HANDLERS
  // ============================================
  const handleOpenAuth = useCallback((mode = 'signin') => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  }, []);

  const handleCloseAuth = useCallback(() => {
    setAuthModalOpen(false);
  }, []);

  const handleAuthSuccess = useCallback(() => {
    setAuthModalOpen(false);
    refreshSessions(); // Refresh sessions after login (device sessions transferred)
  }, [refreshSessions]);

  // ============================================
  // SESSION HANDLERS
  // ============================================
  const handleNewSession = useCallback(() => {
    newSession();
    setDocuments([]);
  }, [newSession]);

  const handleSessionSelect = useCallback((session) => {
    selectSession(session);
    setDocuments([]); // Reset documents for new session
  }, [selectSession]);

  const handleDeleteSession = useCallback((sessionId) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) {
      return;
    }
    deleteSession(sessionId);
    setDocuments([]);
  }, [deleteSession]);

  // ============================================
  // CHAT HANDLERS
  // ============================================
  const handleSend = useCallback(async () => {
    if (!input.trim() || isSending) return;

    // Check limits
    if (usage && usage.remaining <= 0) {
      if (!isLoggedIn) {
        handleOpenAuth('signup');
        return;
      }
      // Could show upgrade modal here
      alert('Daily limit reached. Upgrade for more messages!');
      return;
    }

    const content = input;
    setInput('');

    // If no active session, create one
    if (!activeSession) {
      newSession();
    }

    await sendMessage(content);
  }, [input, isSending, usage, isLoggedIn, activeSession, newSession, sendMessage, handleOpenAuth]);

  const handleSuggestionClick = useCallback(
    (text) => {
      if (!activeSession) {
        handleNewSession();
      }
      setInput(text);
    },
    [activeSession, handleNewSession]
  );

  const handleAttach = useCallback(() => {
    console.log('Attach file clicked');
    // File attachment logic
  }, []);

  // ============================================
  // DOCUMENT HANDLERS
  // ============================================
  const handleDocumentSelect = useCallback((doc) => {
    console.log('Document selected:', doc);
  }, []);

  const handleDocumentDownload = useCallback((doc) => {
    console.log('Download document:', doc);
  }, []);

  const handleDocumentDelete = useCallback((docId) => {
    setDocuments((prev) => prev.filter((d) => d.id !== docId));
  }, []);

  const handleRefreshDocuments = useCallback(() => {
    console.log('Refresh documents');
  }, []);

  // ============================================
  // SETTINGS HANDLER
  // ============================================
  const handleOpenSettings = useCallback(() => {
    if (!isLoggedIn) {
      handleOpenAuth('signin');
    } else {
      setSettingsModalOpen(true);
    }
  }, [isLoggedIn, handleOpenAuth]);

  // ============================================
  // PROFILE HANDLERS
  // ============================================
  const handleUpdateProfile = useCallback(async (data) => {
    // Import from services if not already available
    const { updateProfile } = await import('./services/authService');
    return updateProfile(data);
  }, []);

  const handleChangePassword = useCallback(async (oldPassword, newPassword) => {
    const { changePassword } = await import('./services/authService');
    return changePassword(oldPassword, newPassword);
  }, []);

  const handleSelectPlan = useCallback(async (planId) => {
    if (planId === 'manage') {
      // Open Stripe billing portal
      const { api } = await import('./services/api');
      const result = await api.apiRequest('/accounts/stripe/portal/', {
        method: 'POST',
        body: JSON.stringify({ return_url: window.location.href }),
      });
      if (result.data?.portal_url) {
        window.location.href = result.data.portal_url;
      }
    } else {
      // Create checkout session for upgrade
      const { api } = await import('./services/api');
      const result = await api.apiRequest('/accounts/stripe/checkout/', {
        method: 'POST',
        body: JSON.stringify({
          tier: planId,
          success_url: window.location.origin + '/success',
          cancel_url: window.location.href,
        }),
      });
      if (result.data?.checkout_url) {
        window.location.href = result.data.checkout_url;
      }
    }
  }, []);

  // ============================================
  // RENDER
  // ============================================

  // Show loading state while initializing
  if (authLoading || initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Loading GhostInk...</p>
        </div>
        <style>{`
          .loading-screen {
            height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--gi-gray-950, #0a0a0f);
            color: var(--gi-gray-400, #9ca3af);
          }
          .loading-content {
            text-align: center;
          }
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid var(--gi-gray-800, #1f2937);
            border-top-color: var(--gi-green-500, #22c55e);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 16px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <ThreeColumnLayout
        // Left Sidebar
        leftSidebar={
          <SessionSidebar
            sessions={sessions}
            currentSessionId={activeSession?.id}
            onSessionSelect={handleSessionSelect}
            onNewSession={handleNewSession}
            onDeleteSession={handleDeleteSession}
            onPinSession={pinSession}
            onArchiveSession={archiveSession}
            onRenameSession={renameSession}
            onClose={() => setLeftSidebarOpen(false)}
            onOpenSettings={handleOpenSettings}
            // Pass auth info for sidebar footer
            user={user}
            usage={usage}
            onLogin={() => handleOpenAuth('signin')}
            onSignup={() => handleOpenAuth('signup')}
            onLogout={logout}
          />
        }
        leftOpen={leftSidebarOpen}
        onLeftToggle={setLeftSidebarOpen}
        leftWidth={leftWidth}
        onLeftWidthChange={setLeftWidth}
        // Main Content
        mainContent={
          <ChatInterface
            messages={messages}
            isLoading={isSending}
            input={input}
            onInputChange={setInput}
            onSend={handleSend}
            onAttach={handleAttach}
            onSuggestionClick={handleSuggestionClick}
            activeSession={activeSession}
            onToggleLeftSidebar={() => setLeftSidebarOpen((prev) => !prev)}
            onToggleRightSidebar={() => setRightSidebarOpen((prev) => !prev)}
            error={chatError}
            onClearError={clearError}
            // Usage info for limit warnings
            usage={usage}
            isLoggedIn={isLoggedIn}
            onAuthRequired={() => handleOpenAuth('signup')}
          />
        }
        // Right Sidebar
        rightSidebar={
          <RightSidebarPanel
            documents={documents}
            onClose={() => setRightSidebarOpen(false)}
            onDocumentSelect={handleDocumentSelect}
            onDocumentDownload={handleDocumentDownload}
            onDocumentDelete={handleDocumentDelete}
            onRefresh={handleRefreshDocuments}
            isExpanded={rightSidebarExpanded}
            onToggleExpand={() => setRightSidebarExpanded((prev) => !prev)}
          />
        }
        rightOpen={rightSidebarOpen}
        onRightToggle={setRightSidebarOpen}
        rightWidth={rightWidth}
        onRightWidthChange={setRightWidth}
        rightExpanded={rightSidebarExpanded}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={handleCloseAuth}
        mode={authModalMode}
        onModeChange={setAuthModalMode}
        onLogin={login}
        onRegister={register}
        onSuccess={handleAuthSuccess}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        user={user}
        usage={usage}
        onUpdateProfile={handleUpdateProfile}
        onChangePassword={handleChangePassword}
        onSelectPlan={handleSelectPlan}
      />
    </>
  );
}
